/**
 * Language: English and Arabic.
 *
 * The English sentence is the key. `t("Open work orders")` returns the Arabic
 * when the person reads Arabic and a translation exists, and the English
 * otherwise — so a screen never shows a key or a blank, and wrapping a string
 * never changes what an English reader sees.
 *
 * Translations live in `src/i18n/ar/*.ts`, one file per area of the product,
 * each exporting a plain record. They are gathered here at build time, so
 * several areas can be translated at once without touching the same file.
 * `i18n.test.ts` fails when a wrapped literal has no Arabic, or when an
 * Arabic entry loses a `{placeholder}` its English carries.
 *
 * Three rules, and why:
 *
 * **Translate where the text is shown, not where it is defined.** Menus,
 * suites and engines build English at import time; wrapping the constant
 * would freeze whichever language was active when the module loaded. The
 * render site calls `t(screen.label)`.
 *
 * **Digits stay Western, the calendar Gregorian.** `ar-SA-u-nu-latn-ca-gregory`:
 * aviation figures, work-order numbers and regulatory dates are read against
 * documents that use them, and a runway called ١٧/٣٥ is a runway nobody on the
 * radio recognises.
 *
 * **The airfield is not mirrored.** Arabic sets `dir="rtl"` on the page, and
 * the layout follows through logical CSS properties — but a map, a chart or a
 * drawing marks itself `dir="ltr"`, because north does not move when the
 * reader's language does.
 */
import { Fragment, useSyncExternalStore, type ReactNode } from "react";

export type Lang = "en" | "ar";

const KEY = "sky.lang";

function readLang(): Lang {
  try {
    return localStorage.getItem(KEY) === "ar" ? "ar" : "en";
  } catch {
    return "en";
  }
}

let current: Lang = readLang();
const listeners = new Set<() => void>();

function applyToDocument(lang: Lang) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
}
applyToDocument(current);

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  if (lang === current) return;
  current = lang;
  try {
    if (lang === "en") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, lang);
  } catch {
    /* storage unavailable: the choice lasts for this page */
  }
  applyToDocument(lang);
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ── the dictionary ─────────────────────────────────────────────────────── */

const modules = import.meta.glob<{ default: Record<string, string> }>("./ar/*.ts", { eager: true });

/** Every Arabic entry, merged. A key defined in two files is a mistake the test names. */
export const AR: Readonly<Record<string, string>> = Object.assign({}, ...Object.values(modules).map((m) => m.default));

/** Where each key came from, for the duplicate check. */
export const AR_SOURCES: ReadonlyArray<[file: string, keys: string[]]> = Object.entries(modules).map(([f, m]) => [
  f,
  Object.keys(m.default),
]);

const missing = new Set<string>();

/** Keys asked for in this session with no Arabic. Settings shows the count. */
export function missingTranslations(): string[] {
  return [...missing];
}

function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole));
}

/**
 * Translate an English sentence. `{name}` placeholders are filled from `vars`
 * in either language.
 */
export function t(en: string, vars?: Record<string, string | number>): string {
  if (current === "ar") {
    const ar = AR[en];
    if (ar !== undefined) return fill(ar, vars);
    // already Arabic: a component translating what its caller translated
    if (!ARABIC.test(en)) missing.add(en);
  }
  return fill(en, vars);
}

const ARABIC = /[\u0600-\u06FF]/;

/**
 * The same English meaning two things. "Operations" is a menu suite and an
 * access level; "Target" is a KPI's goal and the map's aiming mode. The Arabic
 * differs, so the key carries the context — `"Target@map"` — and the English
 * reader sees the plain word. Without an entry for the context, the plain
 * word's Arabic is used.
 */
export function tc(en: string, context: string, vars?: Record<string, string | number>): string {
  if (current === "ar") {
    const ar = AR[`${en}@${context}`];
    if (ar !== undefined) return fill(ar, vars);
  }
  return t(en, vars);
}

/**
 * A count with its noun. English takes `one` and `other`. Arabic has six
 * forms — zero, one, two, few (3–10), many (11–99), other — so its entry,
 * keyed `"<one>|<other>"`, holds six forms separated by `|`, each of which may
 * use `{n}`. An Arabic entry with fewer forms uses its last for the rest.
 */
export function tn(n: number, one: string, other: string): string {
  if (current === "ar") {
    const entry = AR[`${one}|${other}`];
    if (entry !== undefined) {
      const forms = entry.split("|");
      const order = ["zero", "one", "two", "few", "many", "other"];
      const rule = n === 0 ? "zero" : new Intl.PluralRules("ar").select(n);
      const form = forms[order.indexOf(rule)] ?? forms[forms.length - 1];
      return fill(form, { n });
    }
    missing.add(`${one}|${other}`);
  }
  return fill(n === 1 ? one : other, { n });
}

/** The locale for Intl: Arabic with Western digits and the Gregorian calendar. */
export function locale(): string | undefined {
  return current === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : undefined;
}

/* ── React ──────────────────────────────────────────────────────────────── */

export interface I18n {
  lang: Lang;
  dir: "ltr" | "rtl";
  setLang: (lang: Lang) => void;
  t: typeof t;
  tn: typeof tn;
  tc: typeof tc;
}

/** Re-renders on a change of language. */
export function useI18n(): I18n {
  const lang = useSyncExternalStore(subscribe, getLang, getLang);
  return { lang, dir: lang === "ar" ? "rtl" : "ltr", setLang, t, tn, tc };
}

/**
 * Wraps the application. A change of language remounts what is beneath it, so
 * every `t()` in every component runs again — a language switch is rare
 * enough that doing it once, completely, beats threading the language through
 * every memo. Queries live above this, so nothing is fetched twice.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const lang = useSyncExternalStore(subscribe, getLang, getLang);
  return <Fragment key={lang}>{children}</Fragment>;
}
